import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { API, useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import ProductImage from "@/components/ProductImage";
import { FunnelSimple, MagnifyingGlass, Leaf, Package, Fire, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const COUNTRIES = ["India", "Sri Lanka", "Iran", "Madagascar", "Grenada", "Vietnam", "Ethiopia"];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const { user, authHeaders } = useAuth();
  const { format, code } = useCurrency();

  const filters = useMemo(() => ({
    category: params.get("category") || "",
    country: params.get("country") || "",
    grade: params.get("grade") || "",
    organic: params.get("organic") === "true",
    min_stock: params.get("min_stock") || "",
    sort: params.get("sort") || "",
    q: params.get("q") || "",
    parent: params.get("parent") || "",
  }), [params]);

  useEffect(() => {
    axios.get(`${API}/products/categories`).then((r) => setCats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const query = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== false && v !== null) query[k] = v;
    });
    axios.get(`${API}/products`, { params: query })
      .then((r) => { setProducts(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [filters]);

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "" || v === null || v === false) next.delete(k);
    else next.set(k, String(v));
    setParams(next);
  };

  const addToCart = async (p) => {
    if (!user) { toast.error("Sign in to add to cart"); return; }
    if (user.role !== "buyer") { toast.error("Buyers only can purchase"); return; }
    try {
      await axios.post(`${API}/cart/add`, { product_id: p.id, qty_kg: 10 }, { headers: authHeaders(), withCredentials: true });
      toast.success(`Added ${p.branded_name} (10kg)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="shop-page">
      {/* Hero strip */}
      <section className="bg-ink-900 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14">
          <p className="section-label">The Marketplace</p>
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter mt-3 leading-[1.0]">
            {filters.category ? filters.category : "All spices. All origins."}
          </h1>
          <p className="text-muted mt-3 max-w-xl">Verified grades. Escrow payments. Zero middlemen. Showing <span className="text-cream">{total}</span> listings.</p>

          <div className="mt-8 glass rounded-full flex items-center gap-3 pl-5 pr-2 py-2 max-w-xl">
            <MagnifyingGlass size={18} className="text-muted" />
            <input
              className="flex-1 bg-transparent outline-none text-cream text-sm placeholder:text-muted"
              placeholder="Search pepper, saffron, origin..."
              value={filters.q}
              onChange={(e) => update("q", e.target.value)}
              data-testid="search-input"
            />
            <select
              className="bg-ink-800 rounded-full text-xs px-3 py-2 text-cream/80 border border-white/10"
              value={filters.sort}
              onChange={(e) => update("sort", e.target.value)}
              data-testid="sort-select"
            >
              <option value="">Newest</option>
              <option value="price_asc">Price ↑</option>
              <option value="price_desc">Price ↓</option>
              <option value="grade">Grade</option>
            </select>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid lg:grid-cols-12 gap-8">
        {/* Filters */}
        <aside className="lg:col-span-3">
          <div className="glass rounded-2xl p-6 space-y-7 sticky top-24">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl text-cream flex items-center gap-2"><FunnelSimple weight="duotone" /> Filters</h3>
              {Array.from(params.keys()).length > 0 && (
                <button onClick={() => setParams(new URLSearchParams())} className="text-xs text-muted hover:text-saffron" data-testid="clear-filters">Clear</button>
              )}
            </div>

            <FilterBlock title="Category">
              <div className="flex flex-wrap gap-2">
                {cats.map((c) => (
                  <button key={c.category} onClick={() => update("category", filters.category === c.category ? "" : c.category)}
                    className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${filters.category === c.category ? "bg-saffron text-ink-950 border-saffron" : "border-white/10 text-cream/80 hover:border-gold/40"}`}
                    data-testid={`filter-cat-${c.category}`}>
                    {c.category}
                    <span className="text-muted ml-1">{c.count}</span>
                  </button>
                ))}
              </div>
            </FilterBlock>

            <FilterBlock title="Origin">
              <div className="flex flex-wrap gap-2">
                {COUNTRIES.map((c) => (
                  <button key={c} onClick={() => update("country", filters.country === c ? "" : c)}
                    className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${filters.country === c ? "bg-gold/20 border-gold text-gold" : "border-white/10 text-cream/80 hover:border-gold/40"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </FilterBlock>

            <FilterBlock title="Grade">
              <div className="flex gap-2">
                {["A", "B", "C"].map((g) => (
                  <button key={g} onClick={() => update("grade", filters.grade === g ? "" : g)}
                    className={`text-xs rounded-full px-4 py-1.5 border ${filters.grade === g ? "bg-saffron text-ink-950 border-saffron" : "border-white/10 text-cream/80"}`}>
                    Grade {g}
                  </button>
                ))}
              </div>
            </FilterBlock>

            <FilterBlock title="Attributes">
              <label className="flex items-center gap-2 text-sm text-cream/80">
                <input type="checkbox" checked={filters.organic} onChange={(e) => update("organic", e.target.checked)} data-testid="filter-organic" />
                Organic only
              </label>
            </FilterBlock>

            <FilterBlock title="Min stock">
              <div className="flex gap-2 flex-wrap">
                {["", "100", "500", "1000"].map((s) => (
                  <button key={s} onClick={() => update("min_stock", s)}
                    className={`text-xs rounded-full px-3 py-1.5 border ${filters.min_stock === s ? "bg-forest text-cream border-forest" : "border-white/10 text-cream/80"}`}>
                    {s === "" ? "Any" : `>${s} kg`}
                  </button>
                ))}
              </div>
            </FilterBlock>
          </div>
        </aside>

        {/* Grid */}
        <section className="lg:col-span-9">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[4/5] rounded-2xl bg-ink-800 animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="glass rounded-2xl p-16 text-center" data-testid="no-products">
              <Package size={32} className="text-muted mx-auto" />
              <h3 className="font-display text-2xl mt-4">No products match.</h3>
              <p className="text-muted mt-2 text-sm">Try clearing filters or adjusting grade.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-7">
              {products.map((p, i) => (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: (i % 9) * 0.04 }}
                  className="group"
                  data-testid={`product-card-${p.id}`}
                >
                  <Link to={`/product/${p.id}`} className="block aspect-[4/5] rounded-2xl overflow-hidden bg-ink-800 relative">
                    <ProductImage src={p.images?.[0]} alt={p.branded_name} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute top-3 left-3 right-3 flex flex-wrap gap-1.5 pointer-events-none">
                      <span className="text-[9px] uppercase tracking-[0.15em] text-gold border border-gold/40 rounded-full px-2 py-0.5 bg-ink-950/70 backdrop-blur-sm">Grade {p.grade}</span>
                      {p.organic && <span className="text-[9px] uppercase tracking-[0.15em] text-cream border border-forest/60 rounded-full px-2 py-0.5 bg-forest/40 backdrop-blur-sm flex items-center gap-1"><Leaf size={9} weight="fill" />Organic</span>}
                    </div>
                    <div className="absolute bottom-3 left-3 right-3 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                      <button onClick={(e) => { e.preventDefault(); addToCart(p); }} className="w-full bg-saffron hover:bg-saffron-deep text-ink-950 rounded-full text-xs font-medium py-2.5" data-testid={`add-cart-${p.id}`}>
                        Quick add · 10kg
                      </button>
                    </div>
                  </Link>
                  <div className="mt-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gold">{p.country} · {p.category}</p>
                    <h3 className="font-display text-xl md:text-2xl text-cream mt-1 leading-tight">{p.branded_name}</h3>
                    <div className="mt-2 flex items-end justify-between">
                      <div>
                        <span className="font-display text-xl text-saffron">{format(p.price_usd)}</span>
                        <span className="text-xs text-muted">/kg {code}</span>
                      </div>
                      <span className="text-xs text-muted">{Math.round(p.stock_kg)} kg avail.</span>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function FilterBlock({ title, children }) {
  return (
    <div>
      <div className="section-label mb-3">{title}</div>
      {children}
    </div>
  );
}
